export const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Phoenix", label: "Arizona Time (MST)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
  { value: "UTC", label: "UTC" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Central European Time (CET)" },
  { value: "Asia/Kolkata", label: "India Standard Time (IST)" },
  { value: "Asia/Dubai", label: "Gulf Standard Time (GST)" },
  { value: "Asia/Singapore", label: "Singapore Time (SGT)" },
  { value: "Australia/Sydney", label: "Sydney Time (AET)" },
];

export const detectDefaultTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

// The single source of truth for "whose timezone are we displaying in" across the app —
// always the viewing device/browser's IANA zone, never the zone originally chosen at scheduling time.
export const BROWSER_TIMEZONE = detectDefaultTimezone();

export const timezoneOptionsWithDefault = (tz) => {
  if (TIMEZONE_OPTIONS.some((option) => option.value === tz)) return TIMEZONE_OPTIONS;
  return [{ value: tz, label: tz }, ...TIMEZONE_OPTIONS];
};

export const getTimezoneLabel = (tz) => TIMEZONE_OPTIONS.find((option) => option.value === tz)?.label || tz;

// "Eastern Time (America/New_York)" style label, so the displayed timezone name always
// matches the IANA zone actually used to convert the displayed time.
export const formatDisplayTimezoneLabel = (tz) => {
  const entry = TIMEZONE_OPTIONS.find((option) => option.value === tz);
  if (!entry) return tz;
  const name = entry.label.replace(/\s*\([^)]*\)\s*$/, "");
  return `${name} (${tz})`;
};

// Returns the wall-clock date/time parts of `isoString` AS RENDERED IN `tz` — never the viewer's
// browser timezone. This is what keeps calendar placement and drawer display correct everywhere.
export const zonedParts = (isoString, tz) => {
  const date = new Date(isoString);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const map = {};
  parts.forEach((p) => {
    map[p.type] = p.value;
  });
  const hour = map.hour === "24" ? 0 : Number(map.hour);
  return { year: Number(map.year), month: Number(map.month), day: Number(map.day), hour, minute: Number(map.minute) };
};

export const formatZonedTime = (isoString, tz) => {
  const date = new Date(isoString);
  return new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true }).format(date);
};

export const formatZonedDate = (isoString, tz) => {
  const date = new Date(isoString);
  return new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", month: "short", day: "numeric", year: "numeric" }).format(date);
};

export const formatTimeRange = (startIso, endIso, tz, durationMinutes) => {
  const start = formatZonedTime(startIso, tz);
  const end = formatZonedTime(endIso, tz);
  return `${start} – ${end} (${durationMinutes} min)`;
};
